import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Avatar,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  AssignmentTurnedIn as AssignIcon,
  VerifiedUser as QualityIcon,
  OpenInNew as OpenInNewIcon,
  HowToVote as ApproveIcon,
  ArrowForward as ArrowIcon,
  Send as SendIcon,
  Factory as FactoryIcon,
  CheckCircleOutline as ReviewedIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';
import bulkQualityManagementService from '../services/bulkQualityManagement.service';

// ── Types ─────────────────────────────────────────────────────────────────────

interface TrelloCard {
  id: string;
  name: string;
  desc: string;
  labels: Array<{ id: string; name: string; color: string }>;
  due: string | null;
  url: string;
  listId: string;
  listName: string;
  boardId: string;
  boardName: string;
}

interface BulkQualityReview {
  id: number;
  trello_card_id: string;
  trello_card_name: string;
  batch_number: string;
  source_list_name: string;
  source_board_name: string;
  rejection_type: 'internal' | 'external';
  ocl_sub_type: 'internal' | 'external' | null;
  machine_type: 'rotary' | 'digital';
  issue_type: 'color' | 'layout' | null;
  external_issues: string[] | null;
  external_other_issue: string | null;
  description: string | null;
  action_plan: string | null;
  assigned_to: string;
  status: 'pending' | 'in_review' | 'reviewed' | 'submitted_for_approval' | 'aloka_approved' | 'ashan_approved' | 'isuru_approved' | 'resolved';
  aloka_approved_at: string | null;
  aloka_approved_by: string | null;
  ashan_approved_at: string | null;
  ashan_approved_by: string | null;
  isuru_approved_at: string | null;
  isuru_approved_by: string | null;
  lasantha_approved_at: string | null;
  lasantha_approved_by: string | null;
  created_by_name: string;
  created_by_username: string;
  created_at: string;
  updated_at: string;
}

interface Stats {
  total: number;
  pending: number;
  in_review: number;
  reviewed: number;
  submitted_for_approval: number;
  in_approval: number;
  resolved: number;
  internal: number;
  external: number;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const EXTERNAL_ISSUE_OPTIONS = ['Base Color', 'Absorption', 'Hand Feel', 'Other'];

const ASSIGNEE_COLORS: Record<string, string> = {
  KrishanOCL: '#1565C0',
  LahiruOCL:  '#2E7D32',
  AnjanaOCL:  '#6A1B9A',
  AlokaOCL:   '#BF360C',
};

const resolveAssigneeLocal = (
  rejectionType: 'internal' | 'external',
  oclSubType: 'internal' | 'external' | '',
  machineType: 'rotary' | 'digital',
  issueType?: 'color' | 'layout' | ''
): string => {
  if (rejectionType === 'external') return 'AlokaOCL';
  if (oclSubType === 'external') return 'AlokaOCL';
  // OCL internal sub-type
  if (issueType === 'color') return machineType === 'digital' ? 'KrishanOCL' : 'LahiruOCL';
  return machineType === 'digital' ? 'KrishanOCL' : 'AnjanaOCL';
};

const APPROVAL_CHAIN = [
  { username: 'AlokaOCL',    label: 'Aloka',    tsField: 'aloka_approved_at'    as const, byField: 'aloka_approved_by'    as const },
  { username: 'AshanOCL',    label: 'Ashan',    tsField: 'ashan_approved_at'    as const, byField: 'ashan_approved_by'    as const },
  { username: 'IsuruOCL',    label: 'Isuru',    tsField: 'isuru_approved_at'    as const, byField: 'isuru_approved_by'    as const },
  { username: 'LasanthaOCL', label: 'Lasantha', tsField: 'lasantha_approved_at' as const, byField: 'lasantha_approved_by' as const },
];

const STATUS_FLOW = [
  'pending', 'in_review', 'reviewed',
  'submitted_for_approval',
  'aloka_approved', 'ashan_approved', 'isuru_approved', 'resolved',
];

const statusColor = (s: string) => {
  if (s === 'resolved')                return 'success';
  if (s === 'reviewed')                return 'warning';
  if (s === 'in_review')               return 'warning';
  if (s === 'submitted_for_approval')  return 'info';
  if (s.endsWith('_approved'))         return 'secondary';
  return 'default';
};

const statusLabel = (s: string) => {
  if (s === 'submitted_for_approval') return 'Sent for Approval';
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const rejectionDisplayLabel = (r: BulkQualityReview): string => {
  if (r.rejection_type === 'external') return 'Customer - External';
  if (r.ocl_sub_type === 'external')   return 'OCL Internal · External';
  return 'OCL Internal · Internal';
};

// ── Approval Chain Display ─────────────────────────────────────────────────────

const ApprovalChainDisplay: React.FC<{ review: BulkQualityReview }> = ({ review }) => (
  <Box>
    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Approval Chain</Typography>
    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
      {APPROVAL_CHAIN.map((step, i) => {
        const approved = !!review[step.tsField];
        const isNext = !approved && APPROVAL_CHAIN.slice(0, i).every((s) => !!review[s.tsField])
          && ['submitted_for_approval', 'aloka_approved', 'ashan_approved', 'isuru_approved'].includes(review.status);
        return (
          <React.Fragment key={step.username}>
            <Box
              sx={{
                px: 1.5, py: 0.75, borderRadius: 1,
                border: `1px solid ${approved ? '#2E7D32' : isNext ? '#1565C0' : '#bbb'}`,
                backgroundColor: approved ? '#E8F5E9' : isNext ? '#E3F2FD' : '#fafafa',
                textAlign: 'center', minWidth: 80,
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, color: approved ? '#2E7D32' : isNext ? '#1565C0' : '#999' }}>
                {approved ? '✓ ' : isNext ? '◎ ' : '○ '}{step.label}
              </Typography>
              {approved && review[step.tsField] && (
                <>
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                    {new Date(review[step.tsField]!).toLocaleDateString('en-GB')}{' '}
                    {new Date(review[step.tsField]!).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                  {review[step.byField] && (
                    <Typography variant="caption" display="block" sx={{ fontSize: '0.6rem', color: '#2E7D32', fontWeight: 600 }}>
                      by {review[step.byField]}
                    </Typography>
                  )}
                </>
              )}
              {isNext && (
                <Typography variant="caption" display="block" sx={{ color: '#1565C0', fontSize: '0.6rem' }}>Pending</Typography>
              )}
            </Box>
            {i < APPROVAL_CHAIN.length - 1 && <ArrowIcon sx={{ color: '#bbb', fontSize: 16 }} />}
          </React.Fragment>
        );
      })}
    </Box>
  </Box>
);

const StatCard: React.FC<{ label: string; value: number | string; color?: string }> = ({ label, value, color = '#00695C' }) => (
  <Card elevation={2}>
    <CardContent sx={{ textAlign: 'center', py: 2 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, color }}>{value}</Typography>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </CardContent>
  </Card>
);

// ── Main Component ─────────────────────────────────────────────────────────────

const BulkQualityManagement: React.FC = () => {
  const user = useAuthStore((s) => s.user);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // tabs: 0=New Review, 1=My Assignments, 2=All Reviews, 3=Pending Approvals
  const [tab, setTab] = useState(0);

  // ── Search ─────────────────────────────────────────────────────────────────
  const [searchInput, setSearchInput]   = useState('');
  const [searching, setSearching]       = useState(false);
  const [searchError, setSearchError]   = useState<string | null>(null);
  const [foundCards, setFoundCards]     = useState<TrelloCard[]>([]);
  const [selectedCard, setSelectedCard] = useState<TrelloCard | null>(null);
  const [searchedIn, setSearchedIn]     = useState<string[]>([]);
  const [manualEntry, setManualEntry]   = useState(false);

  // ── Form ───────────────────────────────────────────────────────────────────
  const [rejectionType, setRejectionType]     = useState<'internal' | 'external' | ''>('');
  const [oclSubType, setOclSubType]           = useState<'internal' | 'external' | ''>('');
  const [machineType, setMachineType]         = useState<'rotary' | 'digital' | ''>('');
  const [issueType, setIssueType]             = useState<'color' | 'layout' | ''>('');
  const [externalIssues, setExternalIssues]   = useState<string[]>([]);
  const [externalOtherText, setExternalOtherText] = useState('');
  const [description, setDescription]         = useState('');
  const [actionPlan, setActionPlan]           = useState('');

  // ── Save ───────────────────────────────────────────────────────────────────
  const [saving, setSaving]           = useState(false);
  const [saveError, setSaveError]     = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [lastCreated, setLastCreated] = useState<{ review: BulkQualityReview; assignedTo: string } | null>(null);

  // ── Reviews list ───────────────────────────────────────────────────────────
  const [reviews, setReviews]                   = useState<BulkQualityReview[]>([]);
  const [myAssignments, setMyAssignments]       = useState<BulkQualityReview[]>([]);
  const [pendingApprovals, setPendingApprovals] = useState<BulkQualityReview[]>([]);
  const [stats, setStats]                       = useState<Stats | null>(null);
  const [loadingReviews, setLoadingReviews]     = useState(false);
  const [viewDialogOpen, setViewDialogOpen]     = useState(false);
  const [viewReview, setViewReview]             = useState<BulkQualityReview | null>(null);

  // ── Approval / Send-for-approval ───────────────────────────────────────────
  const [approvingId, setApprovingId]         = useState<number | null>(null);
  const [sendingApprovalId, setSendingApprovalId] = useState<number | null>(null);
  const [cardStatusMap, setCardStatusMap]     = useState<Record<number, { inPrintingOut: boolean; currentList: string; loading: boolean }>>({});

  // ── Computed ───────────────────────────────────────────────────────────────
  const effectiveRejectionType: 'internal' | 'external' | '' =
    rejectionType === 'internal' && oclSubType ? oclSubType : rejectionType;
  const effectiveIssueType = rejectionType === 'internal' && oclSubType === 'internal' ? issueType : '';

  const preview = effectiveRejectionType && machineType
    ? resolveAssigneeLocal(
        effectiveRejectionType as 'internal' | 'external',
        oclSubType,
        machineType as 'rotary' | 'digital',
        effectiveIssueType as 'color' | 'layout' | ''
      )
    : null;

  const canSubmit =
    (!!selectedCard || manualEntry) &&
    !!rejectionType && !!machineType && !!oclSubType &&
    (oclSubType === 'external' ? externalIssues.length > 0 : !!issueType);

  // ── Load helpers ───────────────────────────────────────────────────────────
  const loadStats = useCallback(async () => {
    try {
      const data = await bulkQualityManagementService.getStats();
      setStats(data.stats);
    } catch (_) {}
  }, []);

  const loadMyAssignments = useCallback(async () => {
    setLoadingReviews(true);
    try {
      const data = await bulkQualityManagementService.getMyAssignments();
      setMyAssignments(data.reviews || []);
    } catch (_) {} finally { setLoadingReviews(false); }
  }, []);

  const loadAllReviews = useCallback(async () => {
    setLoadingReviews(true);
    try {
      const data = await bulkQualityManagementService.getAll();
      setReviews(data.reviews || []);
    } catch (_) {} finally { setLoadingReviews(false); }
  }, []);

  const loadPendingApprovals = useCallback(async () => {
    setLoadingReviews(true);
    try {
      const data = await bulkQualityManagementService.getPendingApprovals();
      setPendingApprovals(data.reviews || []);
    } catch (_) {} finally { setLoadingReviews(false); }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  useEffect(() => {
    if (tab === 1) loadMyAssignments();
    else if (tab === 2) loadAllReviews();
    else if (tab === 3) loadPendingApprovals();
  }, [tab, loadMyAssignments, loadAllReviews, loadPendingApprovals]);

  // ── Search ─────────────────────────────────────────────────────────────────
  const handleSearch = async () => {
    if (!searchInput.trim()) return;
    setSearching(true); setSearchError(null); setFoundCards([]); setSelectedCard(null);
    try {
      const data = await bulkQualityManagementService.searchCard(searchInput.trim());
      setFoundCards(data.cards || []);
      setSearchedIn(data.searched_in || []);
      if (!data.found) setSearchError('No cards found matching that batch number on the Bulk board.');
    } catch (e: any) {
      setSearchError(e?.response?.data?.error || 'Search failed');
    } finally { setSearching(false); }
  };

  const handleExternalIssueToggle = (option: string) => {
    setExternalIssues((prev) =>
      prev.includes(option) ? prev.filter((o) => o !== option) : [...prev, option]
    );
  };

  const resetForm = () => {
    setSearchInput(''); setFoundCards([]); setSelectedCard(null);
    setSearchedIn([]); setManualEntry(false); setSearchError(null);
    setRejectionType(''); setOclSubType(''); setMachineType(''); setIssueType('');
    setExternalIssues([]); setExternalOtherText('');
    setDescription(''); setActionPlan('');
    setSaveError(null); setSaveSuccess(false);
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true); setSaveError(null);
    try {
      const batchNum = manualEntry ? searchInput.trim() : selectedCard!.name;
      const issues = [...externalIssues];
      if (issues.includes('Other') && externalOtherText.trim()) {
        issues[issues.indexOf('Other')] = `Other: ${externalOtherText.trim()}`;
      }
      const payload: any = {
        batch_number:    batchNum,
        rejection_type:  rejectionType as 'internal' | 'external',
        ocl_sub_type:    oclSubType as 'internal' | 'external',
        machine_type:    machineType as 'rotary' | 'digital',
        issue_type:      (oclSubType === 'internal' ? issueType : undefined) as any,
        external_issues: oclSubType === 'external' ? issues : undefined,
        external_other_issue: oclSubType === 'external' && externalOtherText.trim() ? externalOtherText.trim() : undefined,
        description:     description.trim() || undefined,
        action_plan:     actionPlan.trim() || undefined,
      };
      if (!manualEntry && selectedCard) {
        payload.trello_card_id   = selectedCard.id;
        payload.trello_card_name = selectedCard.name;
        payload.source_list_name = selectedCard.listName;
        payload.source_board_name = selectedCard.boardName;
      }
      const result = await bulkQualityManagementService.create(payload);
      setSaveSuccess(true);
      setLastCreated({ review: result.review, assignedTo: result.assigned_to });
      loadStats();
    } catch (e: any) {
      setSaveError(e?.response?.data?.error || 'Failed to submit review');
    } finally { setSaving(false); }
  };

  // ── Status update ──────────────────────────────────────────────────────────
  const handleStatusUpdate = async (id: number, status: string) => {
    try {
      await bulkQualityManagementService.updateStatus(id, status);
      if (tab === 1) loadMyAssignments(); else loadAllReviews();
      if (viewReview?.id === id) {
        const updated = await bulkQualityManagementService.getById(id);
        setViewReview(updated.review);
      }
    } catch (_) {}
  };

  // ── Send for approval ──────────────────────────────────────────────────────
  const handleSendForApproval = async (id: number) => {
    setSendingApprovalId(id);
    try {
      const result = await bulkQualityManagementService.sendForApproval(id);
      if (tab === 1) loadMyAssignments(); else loadAllReviews();
      if (viewReview?.id === id) setViewReview(result.review);
      setCardStatusMap((prev) => {
        const next = { ...prev }; delete next[id]; return next;
      });
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Failed to send for approval');
    } finally { setSendingApprovalId(null); }
  };

  // ── Check card status ──────────────────────────────────────────────────────
  const checkCardStatus = async (id: number) => {
    setCardStatusMap((prev) => ({ ...prev, [id]: { inPrintingOut: false, currentList: '', loading: true } }));
    try {
      const data = await bulkQualityManagementService.checkCardStatus(id);
      setCardStatusMap((prev) => ({ ...prev, [id]: { ...data, loading: false } }));
    } catch (_) {
      setCardStatusMap((prev) => ({ ...prev, [id]: { inPrintingOut: false, currentList: 'Check failed', loading: false } }));
    }
  };

  // ── Approve ────────────────────────────────────────────────────────────────
  const handleApprove = async (id: number) => {
    setApprovingId(id);
    try {
      const result = await bulkQualityManagementService.approve(id);
      loadPendingApprovals(); loadStats();
      if (viewReview?.id === id) setViewReview(result.review);
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Approval failed');
    } finally { setApprovingId(null); }
  };

  // ── Review Card (shared render) ────────────────────────────────────────────
  const canSendForApproval = (r: BulkQualityReview) =>
    (r.assigned_to === user?.username || user?.role === 'admin') &&
    ['in_review', 'reviewed'].includes(r.status);

  const canApprove = (r: BulkQualityReview) => {
    const approverMap: Record<string, string> = {
      'submitted_for_approval': 'AlokaOCL',
      'aloka_approved':         'AshanOCL',
      'ashan_approved':         'IsuruOCL',
      'isuru_approved':         'LasanthaOCL',
    };
    const needed = approverMap[r.status];
    return needed && (user?.username === needed || user?.role === 'admin');
  };

  const renderReviewActions = (r: BulkQualityReview) => (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {/* Mark In Review */}
      {r.status === 'pending' && r.assigned_to === user?.username && (
        <Button size="small" variant="outlined" color="warning"
          onClick={() => handleStatusUpdate(r.id, 'in_review')}>
          Start Review
        </Button>
      )}
      {/* Mark Reviewed */}
      {r.status === 'in_review' && (r.assigned_to === user?.username || user?.role === 'admin') && (
        <Button size="small" variant="outlined" color="success" startIcon={<ReviewedIcon />}
          onClick={() => handleStatusUpdate(r.id, 'reviewed')}>
          Mark Reviewed
        </Button>
      )}
      {/* Check card + Send for Approval */}
      {canSendForApproval(r) && (
        <>
          {!cardStatusMap[r.id] && (
            <Button size="small" variant="outlined" color="info"
              onClick={() => checkCardStatus(r.id)}
              disabled={cardStatusMap[r.id]?.loading}>
              {cardStatusMap[r.id]?.loading ? <CircularProgress size={14} /> : 'Check Card Status'}
            </Button>
          )}
          {cardStatusMap[r.id] && !cardStatusMap[r.id].loading && (
            <Chip
              size="small"
              label={cardStatusMap[r.id].inPrintingOut ? '✓ In Printing Out' : `In: ${cardStatusMap[r.id].currentList}`}
              color={cardStatusMap[r.id].inPrintingOut ? 'success' : 'default'}
              variant="outlined"
            />
          )}
          {(cardStatusMap[r.id]?.inPrintingOut || r.trello_card_id === 'manual') && (
            <Button
              size="small" variant="contained" color="primary" startIcon={<SendIcon />}
              onClick={() => handleSendForApproval(r.id)}
              disabled={sendingApprovalId === r.id}>
              {sendingApprovalId === r.id ? <CircularProgress size={14} /> : 'Send for Approval'}
            </Button>
          )}
        </>
      )}
      {/* Approve */}
      {canApprove(r) && (
        <Button size="small" variant="contained" color="success" startIcon={<ApproveIcon />}
          onClick={() => handleApprove(r.id)}
          disabled={approvingId === r.id}>
          {approvingId === r.id ? <CircularProgress size={14} /> : 'Approve'}
        </Button>
      )}
    </Stack>
  );

  const renderMobileCard = (r: BulkQualityReview) => (
    <Card key={r.id} elevation={2} sx={{ mb: 2 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Typography variant="subtitle1" fontWeight={700}>{r.batch_number}</Typography>
          <Chip size="small" label={statusLabel(r.status)} color={statusColor(r.status) as any} />
        </Stack>
        <Typography variant="body2" color="text.secondary" mb={0.5}>{r.trello_card_name}</Typography>
        <Typography variant="body2" mb={0.5}>
          <strong>Type:</strong> <Chip size="small" label={rejectionDisplayLabel(r)} color={r.rejection_type === 'internal' ? 'warning' : 'error'} variant="outlined" />
        </Typography>
        <Typography variant="body2" mb={0.5}><strong>Machine:</strong> {r.machine_type}</Typography>
        <Typography variant="body2" mb={1}><strong>Assigned:</strong>{' '}
          <Avatar sx={{ width: 20, height: 20, fontSize: 10, bgcolor: ASSIGNEE_COLORS[r.assigned_to] || '#455A64', display: 'inline-flex', verticalAlign: 'middle', mr: 0.5 }}>
            {r.assigned_to?.[0]}
          </Avatar>
          {r.assigned_to}
        </Typography>
        {['submitted_for_approval','aloka_approved','ashan_approved','isuru_approved','resolved'].includes(r.status) && (
          <Box mb={1}><ApprovalChainDisplay review={r} /></Box>
        )}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {renderReviewActions(r)}
          <Tooltip title="View details">
            <IconButton size="small" onClick={() => { setViewReview(r); setViewDialogOpen(true); }}>
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </CardContent>
    </Card>
  );

  const renderDesktopTable = (list: BulkQualityReview[]) => (
    <TableContainer component={Paper} elevation={0} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
            <TableCell><strong>Batch</strong></TableCell>
            <TableCell><strong>Card</strong></TableCell>
            <TableCell><strong>Type</strong></TableCell>
            <TableCell><strong>Machine</strong></TableCell>
            <TableCell><strong>Assigned To</strong></TableCell>
            <TableCell><strong>Status</strong></TableCell>
            <TableCell><strong>Submitted</strong></TableCell>
            <TableCell><strong>Actions</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {list.map((r) => (
            <TableRow key={r.id} hover>
              <TableCell sx={{ fontWeight: 600 }}>{r.batch_number}</TableCell>
              <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {r.trello_card_name}
              </TableCell>
              <TableCell>
                <Chip size="small" label={rejectionDisplayLabel(r)} color={r.rejection_type === 'internal' ? 'warning' : 'error'} variant="outlined" />
              </TableCell>
              <TableCell sx={{ textTransform: 'capitalize' }}>{r.machine_type}</TableCell>
              <TableCell>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Avatar sx={{ width: 22, height: 22, fontSize: 11, bgcolor: ASSIGNEE_COLORS[r.assigned_to] || '#455A64' }}>
                    {r.assigned_to?.[0]}
                  </Avatar>
                  <Typography variant="body2">{r.assigned_to}</Typography>
                </Stack>
              </TableCell>
              <TableCell><Chip size="small" label={statusLabel(r.status)} color={statusColor(r.status) as any} /></TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(r.created_at).toLocaleDateString('en-GB')}</TableCell>
              <TableCell>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {renderReviewActions(r)}
                  <Tooltip title="View details">
                    <IconButton size="small" onClick={() => { setViewReview(r); setViewDialogOpen(true); }}>
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // ── Tab 0: New Review form ─────────────────────────────────────────────────
  const renderNewReview = () => {
    if (saveSuccess && lastCreated) {
      return (
        <Paper sx={{ p: 3 }}>
          <Alert severity="success" sx={{ mb: 3 }}>
            Bulk quality review submitted successfully!
          </Alert>
          <Box sx={{ p: 2, border: '2px solid #2E7D32', borderRadius: 2, bgcolor: '#E8F5E9', mb: 3 }}>
            <Typography variant="h6" sx={{ color: '#1B5E20', fontWeight: 700 }}>
              ✓ Assigned to: {lastCreated.assignedTo}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              Batch: <strong>{lastCreated.review.batch_number}</strong> — {lastCreated.review.trello_card_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Status: <strong>Pending</strong> — {lastCreated.assignedTo} has been notified.
            </Typography>
          </Box>
          <Button variant="contained" onClick={() => { resetForm(); setSaveSuccess(false); setLastCreated(null); }}>
            Submit Another Review
          </Button>
        </Paper>
      );
    }

    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={700} mb={3}>
          <FactoryIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#00695C' }} />
          New Bulk Quality Review
        </Typography>

        {/* Step 1: Find card */}
        <Typography variant="subtitle1" fontWeight={700} mb={1} sx={{ color: '#00695C' }}>
          Step 1 — Find Bulk Card
        </Typography>
        <Box display="flex" gap={1} mb={1} flexWrap="wrap">
          <TextField
            size="small" label="Batch / Card Number"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ flexGrow: 1, minWidth: 200 }}
            disabled={searching}
          />
          <Button variant="contained" sx={{ bgcolor: '#00695C', '&:hover': { bgcolor: '#004D40' } }}
            onClick={handleSearch} disabled={searching || !searchInput.trim()} startIcon={searching ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}>
            Search Bulk Board
          </Button>
        </Box>

        {searchError && <Alert severity="warning" sx={{ mb: 2 }}>{searchError}
          <Button size="small" sx={{ ml: 2 }} onClick={() => setManualEntry(true)}>Enter manually</Button>
        </Alert>}

        {foundCards.length > 0 && !selectedCard && (
          <Box mb={2}>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Found {foundCards.length} card(s) — click to select:
            </Typography>
            {foundCards.map((c) => (
              <Card key={c.id} variant="outlined" sx={{ mb: 1, cursor: 'pointer', '&:hover': { borderColor: '#00695C' } }}
                onClick={() => setSelectedCard(c)}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body1" fontWeight={600}>{c.name}</Typography>
                      <Typography variant="caption" color="text.secondary">List: {c.listName}</Typography>
                    </Box>
                    <Stack direction="row" gap={0.5} flexWrap="wrap">
                      {c.labels.map((l) => <Chip key={l.id} size="small" label={l.name || l.color} />)}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}

        {selectedCard && (
          <Alert severity="success" sx={{ mb: 2 }} action={
            <Button size="small" color="inherit" onClick={() => setSelectedCard(null)}>Change</Button>
          }>
            Selected: <strong>{selectedCard.name}</strong> (from {selectedCard.listName})
          </Alert>
        )}

        {manualEntry && !selectedCard && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Manual entry mode — batch number: <strong>{searchInput.trim()}</strong>
            <Button size="small" sx={{ ml: 2 }} onClick={() => setManualEntry(false)}>Cancel</Button>
          </Alert>
        )}

        {searchedIn.length > 0 && (
          <Typography variant="caption" color="text.secondary" display="block" mb={2}>
            Searched in: {searchedIn.slice(0, 3).join(', ')}{searchedIn.length > 3 ? ` +${searchedIn.length - 3} more` : ''}
          </Typography>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Step 2: Classify */}
        {(selectedCard || manualEntry) && (
          <>
            <Typography variant="subtitle1" fontWeight={700} mb={2} sx={{ color: '#00695C' }}>
              Step 2 — Classify Rejection
            </Typography>
            <Grid container spacing={3}>

              {/* Rejection Type */}
              <Grid item xs={12} sm={6}>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontWeight: 700, color: '#333' }}>Rejection Type *</FormLabel>
                  <RadioGroup row value={rejectionType} onChange={(e) => {
                    setRejectionType(e.target.value as any);
                    setOclSubType(''); setIssueType(''); setExternalIssues([]); setExternalOtherText('');
                  }}>
                    <FormControlLabel value="internal" control={<Radio />} label="OCL - Internal Rejection" />
                    <FormControlLabel value="external" control={<Radio />} label="Customer - External Rejection" />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {/* Machine Type */}
              <Grid item xs={12} sm={6}>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontWeight: 700, color: '#333' }}>Machine Type *</FormLabel>
                  <RadioGroup row value={machineType} onChange={(e) => setMachineType(e.target.value as any)}>
                    <FormControlLabel value="rotary" control={<Radio />} label="Rotary" />
                    <FormControlLabel value="digital" control={<Radio />} label="Digital" />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {/* OCL Sub-Type (shown for both rejection types) */}
              {rejectionType && (
                <Grid item xs={12} sm={6}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend" sx={{ fontWeight: 700, color: '#333' }}>OCL Rejection Sub-Type *</FormLabel>
                    <RadioGroup row value={oclSubType} onChange={(e) => {
                      setOclSubType(e.target.value as any);
                      setIssueType(''); setExternalIssues([]); setExternalOtherText('');
                    }}>
                      <FormControlLabel value="internal" control={<Radio />} label="Internal Rejection" />
                      <FormControlLabel value="external" control={<Radio />} label="External Rejection" />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              )}

              {/* Issue Type (OCL Internal sub-type) */}
              {oclSubType === 'internal' && (
                <Grid item xs={12} sm={6}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend" sx={{ fontWeight: 700, color: '#333' }}>Issue Type *</FormLabel>
                    <RadioGroup row value={issueType} onChange={(e) => setIssueType(e.target.value as any)}>
                      <FormControlLabel value="color" control={<Radio />} label="Color Issue" />
                      <FormControlLabel value="layout" control={<Radio />} label="Layout Issue" />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              )}

              {/* External Issue Checklist */}
              {oclSubType === 'external' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <FormControl component="fieldset">
                      <FormLabel component="legend" sx={{ fontWeight: 700, color: '#333' }}>
                        Issues Identified * (tick all that apply)
                      </FormLabel>
                      <FormGroup>
                        {EXTERNAL_ISSUE_OPTIONS.map((option) => (
                          <FormControlLabel
                            key={option}
                            control={<Checkbox checked={externalIssues.includes(option)} onChange={() => handleExternalIssueToggle(option)} />}
                            label={option}
                          />
                        ))}
                      </FormGroup>
                      {externalIssues.includes('Other') && (
                        <TextField size="small" label="Specify other issue" value={externalOtherText}
                          onChange={(e) => setExternalOtherText(e.target.value)} sx={{ mt: 1 }} fullWidth />
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Description" multiline rows={3} value={description}
                      onChange={(e) => setDescription(e.target.value)} placeholder="Describe the rejection in detail..." />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Action Plan" multiline rows={3} value={actionPlan}
                      onChange={(e) => setActionPlan(e.target.value)} placeholder="Describe the planned corrective action..." />
                  </Grid>
                </>
              )}
            </Grid>

            {/* Assignee Preview */}
            {preview && (
              <Paper sx={{ mt: 3, p: 2, border: `2px solid ${ASSIGNEE_COLORS[preview] || '#455A64'}`, borderRadius: 2, bgcolor: '#fafafa' }}>
                <Typography variant="subtitle2" color="text.secondary">Will be assigned to:</Typography>
                <Stack direction="row" alignItems="center" spacing={1.5} mt={0.5}>
                  <Avatar sx={{ bgcolor: ASSIGNEE_COLORS[preview] || '#455A64', width: 36, height: 36 }}>
                    {preview[0]}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={700} sx={{ color: ASSIGNEE_COLORS[preview] || '#455A64' }}>
                      {preview}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {rejectionType === 'internal'
                        ? oclSubType === 'internal'
                          ? `OCL Internal · ${machineType} · ${issueType} issue`
                          : 'OCL Internal · External Sub-type'
                        : 'Customer - External Rejection'}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            )}

            {saveError && <Alert severity="error" sx={{ mt: 2 }}>{saveError}</Alert>}

            <Box mt={3} display="flex" gap={2}>
              <Button variant="contained" sx={{ bgcolor: '#00695C', '&:hover': { bgcolor: '#004D40' } }}
                onClick={handleSubmit} disabled={!canSubmit || saving}
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <AssignIcon />}>
                {saving ? 'Submitting…' : 'Submit Review'}
              </Button>
              <Button variant="outlined" color="inherit" onClick={resetForm} disabled={saving}>Reset</Button>
            </Box>
          </>
        )}
      </Paper>
    );
  };

  // ── Stats bar ──────────────────────────────────────────────────────────────
  const renderStats = () => (
    <Grid container spacing={2} mb={3}>
      <Grid item xs={6} sm={3} md={2}><StatCard label="Total" value={stats?.total ?? '…'} /></Grid>
      <Grid item xs={6} sm={3} md={2}><StatCard label="Pending" value={stats?.pending ?? '…'} color="#E65100" /></Grid>
      <Grid item xs={6} sm={3} md={2}><StatCard label="In Review" value={stats?.in_review ?? '…'} color="#F57C00" /></Grid>
      <Grid item xs={6} sm={3} md={2}><StatCard label="Reviewed" value={stats?.reviewed ?? '…'} color="#2E7D32" /></Grid>
      <Grid item xs={6} sm={3} md={2}><StatCard label="In Approval" value={stats?.in_approval ?? '…'} color="#1565C0" /></Grid>
      <Grid item xs={6} sm={3} md={2}><StatCard label="Resolved" value={stats?.resolved ?? '…'} color="#00695C" /></Grid>
    </Grid>
  );

  // ── Main render ────────────────────────────────────────────────────────────
  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager';
  // Users who can submit new reviews (create) — admin, manager, or KrishanOCL by username
  const canCreateReview = isAdmin || isManager || user?.username === 'KrishanOCL';

  return (
    <Box>
      {/* Refresh button */}
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Tooltip title="Refresh">
          <IconButton onClick={() => {
            loadStats();
            const canSeeAllReviews = isAdmin || isManager || user?.username === 'KrishanOCL';
            const myAssignmentsTab = canCreateReview ? 1 : 0;
            const allReviewsTab   = canSeeAllReviews ? 2 : -1;
            const pendingTab      = canCreateReview ? (canSeeAllReviews ? 3 : 2) : (canSeeAllReviews ? 2 : 1);
            if (tab === myAssignmentsTab) loadMyAssignments();
            else if (tab === allReviewsTab) loadAllReviews();
            else if (tab === pendingTab) loadPendingApprovals();
          }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Stats */}
      {renderStats()}

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        {canCreateReview && <Tab label="New Review" />}
        <Tab label="My Assignments" />
        {(isAdmin || isManager || user?.username === 'KrishanOCL') && <Tab label="All Reviews" />}
        <Tab label="Pending Approvals" />
      </Tabs>

      {/* Tab content */}
      {tab === 0 && canCreateReview && renderNewReview()}

      {tab === (canCreateReview ? 1 : 0) && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight={700}>My Assignments</Typography>
            <Button size="small" onClick={loadMyAssignments} startIcon={<RefreshIcon />}>Refresh</Button>
          </Box>
          {loadingReviews ? <CircularProgress /> :
            myAssignments.length === 0
              ? <Alert severity="info">No reviews assigned to you.</Alert>
              : isMobile
                ? myAssignments.map(renderMobileCard)
                : renderDesktopTable(myAssignments)}
        </Box>
      )}

      {tab === (isAdmin || isManager || user?.username === 'KrishanOCL' ? 2 : -1) && (isAdmin || isManager || user?.username === 'KrishanOCL') && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight={700}>All Bulk Quality Reviews</Typography>
            <Button size="small" onClick={loadAllReviews} startIcon={<RefreshIcon />}>Refresh</Button>
          </Box>
          {loadingReviews ? <CircularProgress /> :
            reviews.length === 0
              ? <Alert severity="info">No reviews found.</Alert>
              : isMobile
                ? reviews.map(renderMobileCard)
                : renderDesktopTable(reviews)}
        </Box>
      )}

      {tab === (canCreateReview ? (isAdmin || isManager || user?.username === 'KrishanOCL' ? 3 : 2) : 1) && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight={700}>Pending Approvals</Typography>
            <Button size="small" onClick={loadPendingApprovals} startIcon={<RefreshIcon />}>Refresh</Button>
          </Box>
          {loadingReviews ? <CircularProgress /> :
            pendingApprovals.length === 0
              ? <Alert severity="success">No pending approvals for you.</Alert>
              : isMobile
                ? pendingApprovals.map(renderMobileCard)
                : renderDesktopTable(pendingApprovals)}
        </Box>
      )}

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" fontWeight={700}>
              Bulk Quality Review — {viewReview?.batch_number}
            </Typography>
            <Chip size="small" label={viewReview ? statusLabel(viewReview.status) : ''} color={viewReview ? statusColor(viewReview.status) as any : 'default'} />
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {viewReview && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Card Name</Typography>
                <Typography variant="body1">{viewReview.trello_card_name}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Source List</Typography>
                <Typography variant="body1">{viewReview.source_list_name || '—'} / {viewReview.source_board_name || 'Bulk Board'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Rejection Type</Typography>
                <Chip size="small"
                  label={viewReview.rejection_type === 'internal' ? 'OCL - Internal Rejection' : 'Customer - External Rejection'}
                  color={viewReview.rejection_type === 'internal' ? 'warning' : 'error'} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">OCL Sub-Type</Typography>
                <Chip size="small"
                  label={viewReview.ocl_sub_type === 'internal' ? 'Internal Rejection' : viewReview.ocl_sub_type === 'external' ? 'External Rejection' : '—'}
                  color="default" variant="outlined" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Machine Type</Typography>
                <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>{viewReview.machine_type}</Typography>
              </Grid>
              {viewReview.issue_type && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Issue Type</Typography>
                  <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>{viewReview.issue_type} Issue</Typography>
                </Grid>
              )}
              {viewReview.external_issues && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Issues Identified</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" mt={0.5}>
                    {(Array.isArray(viewReview.external_issues) ? viewReview.external_issues : JSON.parse(viewReview.external_issues as any)).map((issue: string) => (
                      <Chip key={issue} size="small" label={issue} color="error" variant="outlined" />
                    ))}
                  </Stack>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Assigned To</Typography>
                <Stack direction="row" alignItems="center" spacing={1} mt={0.5}>
                  <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: ASSIGNEE_COLORS[viewReview.assigned_to] || '#455A64' }}>
                    {viewReview.assigned_to?.[0]}
                  </Avatar>
                  <Typography variant="body1" fontWeight={600}>{viewReview.assigned_to}</Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Submitted By</Typography>
                <Typography variant="body1">{viewReview.created_by_name || viewReview.created_by_username}</Typography>
              </Grid>
              {viewReview.description && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                  <Typography variant="body1">{viewReview.description}</Typography>
                </Grid>
              )}
              {viewReview.action_plan && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Action Plan</Typography>
                  <Typography variant="body1">{viewReview.action_plan}</Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <ApprovalChainDisplay review={viewReview} />
              </Grid>
              <Grid item xs={12}>
                {renderReviewActions(viewReview)}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BulkQualityManagement;
